/* eslint-disable no-console */
/* eslint-disable import/extensions */
/* eslint-disable no-relative-import-paths/no-relative-import-paths */
import { bootstrapApplication } from '@angular/platform-browser';

import { App } from './app/app';
import { appConfig } from './app/app.config';

/**
 * Stale-chunk safety net.
 * After a deploy, an old shell may try to fetch a chunk filename that no longer
 * exists on the CDN. Two failure modes are caught here:
 *   1. The dynamic import rejects outright (ChunkLoadError, "Failed to fetch
 *      dynamically imported module", "Importing a module script failed").
 *   2. The CDN/SPA-fallback returns index.html with a 200 status — the JS
 *      engine then chokes on "<!doctype html>" with "Unexpected token '<'"
 *      (or JSON.parse calls inside vendor code throw the same way).
 *
 * One forced reload picks up the new ngsw.json and the new shell.
 * sessionStorage guard prevents an infinite reload loop if the very next load
 * also fails for the same reason (e.g. CDN is genuinely down).
 */
const CHUNK_RELOAD_KEY = 'chunk-reload-attempt';
const CHUNK_ERROR_PATTERN =
  /ChunkLoadError|Loading chunk|Failed to fetch dynamically imported module|Importing a module script failed|Unexpected token '<'|is not valid JSON|<!doctype/i;

/* On a stale-chunk error, evict the SW + caches before reloading so the new
 * shell isn't served straight back from the dead service worker. Without this
 * step a 504 on /ngsw.json can leave the user pinned to broken cached chunks
 * across reload attempts. */
const purgeServiceWorkerAndCaches = async (): Promise<void> => {
  try {
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    }
    if ('caches' in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
  } catch {
    /* ignore — best-effort cleanup */
  }
};

const handleChunkError = (message: string): void => {
  if (!CHUNK_ERROR_PATTERN.test(message)) return;
  if (sessionStorage.getItem(CHUNK_RELOAD_KEY)) return;
  sessionStorage.setItem(CHUNK_RELOAD_KEY, Date.now().toString());
  void purgeServiceWorkerAndCaches().finally(() => window.location.reload());
};

window.addEventListener('error', (event) => {
  const msg = event.message ?? (event.error instanceof Error ? event.error.message : '');
  handleChunkError(msg);
});
window.addEventListener('unhandledrejection', (event) => {
  const reason = event.reason;
  const message = reason instanceof Error ? reason.message : String(reason ?? '');
  handleChunkError(message);
});

/* Clear the reload guard once the app has been alive for a few seconds. */
setTimeout(() => sessionStorage.removeItem(CHUNK_RELOAD_KEY), 10_000);

bootstrapApplication(App, appConfig).catch((err) => console.error(err));
